import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ImageService } from '@app/core/services/image.service';
import { PromotionService } from '@app/core/services/promotion.service';
import { VimeoApiService } from '@app/core/services/vimeo-api.service';
import * as fromRoot from '@app/reducers';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { forkJoin, from, of } from 'rxjs';
import { catchError, concatMap, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { UserPromotionActions } from '../actions';
import * as fromUserPromotions from '../reducers/promotion.reducer';
import { Image } from '@smash-sdk/image/ejs/10-2019/image';
import { PrepareActions } from '@app/client/uploader/store/actions';


@Injectable()
export class UserPromotionEffects {

  loadPromotion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserPromotionActions.loadPromotion),
      concatMap(({ promotionId, teamId }) => {
        let fn = this.promotionService.getPromotion(promotionId);
        if (teamId) {
          fn = this.promotionService.getTeamPromotion(teamId, promotionId);
        }

        return fn.pipe(
          map(({ promotion }) => {
            return UserPromotionActions.loadPromotionSuccess({ promotion });
          }
          ),
          catchError(error =>
            of(UserPromotionActions.loadPromotionFailure({ errorMessage: error.message }))
          )
        );
      })
    ));

  loadteamPromotions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserPromotionActions.loadTeamPromotions),
      map(action => action.teamId),
      concatMap((teamId) =>
        this.promotionService.listTeamPromotions(teamId).pipe(
          concatMap((promotions) => ([
            UserPromotionActions.loadPromotionsSuccess({ ...promotions }),
            PrepareActions.resetPrepare(),
          ])),
          catchError(error => of(UserPromotionActions.loadPromotionsFailure({ errorMessage: error.message })))
        ))
    ));

  createPromotion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserPromotionActions.createPromotion),
      withLatestFrom(
        this.store$.pipe(select(fromUserPromotions.getPromotionToCreate)),
        this.store$.pipe(select(fromRoot.getActiveTeam)),
        this.store$.pipe(select(fromRoot.getSelectedLanguage)),
      ),
      switchMap(([action, promotion, team, language]: any[]) => {
        const data = {
          title: promotion.title,
          text: promotion.text,
          button: promotion.button,
          teaser: promotion.teaser,
          link: promotion.link,
          role: promotion.role,
          target: promotion.target,
          status: promotion.status,
          language: promotion.language
        };
        return forkJoin([of(action), of(promotion), of(team), this.promotionService.createPromotion(team.id, data)]);
      }),
      switchMap(([action, promotionData, team, { promotion }]: any[]) => {
        const p = [];
        if (promotionData.medias) {
          promotionData.medias.forEach(media => {
            p.push(this.promotionService.addMediasToPromotion(team.id, promotion.id, media));
          });
        }
        return forkJoin([of(action), of(promotionData), of(team), ...p]);
      }),
      switchMap(([action, promotion, team]: any) => {
        return [
          UserPromotionActions.createPromotionSuccess({ promotion }),
          UserPromotionActions.loadTeamPromotions({ teamId: team.id }),
        ];
      }))
  );

  createPromotionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserPromotionActions.createPromotionSuccess),
      withLatestFrom(
        this.store$.pipe(select(fromRoot.getActiveTeam))
      ),
      tap(([action, team]) => this.router.navigateByUrl('/account/team/' + team.id + '/promotions'))
    ),
    { dispatch: false }
  );

  updatePromotion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserPromotionActions.updatePromotion),
      withLatestFrom(
        this.store$.pipe(select(fromUserPromotions.getPromotionToUpdate)),
        this.store$.pipe(select(fromRoot.getActiveTeam))
      ),
      switchMap(([action, promotion, team]: any[]) => {
        const data = {
          title: promotion.title,
          text: promotion.text,
          teaser: promotion.teaser,
          button: promotion.button,
          link: promotion.link,
          language: promotion.language,
          role: promotion.role,
          target: promotion.target,
          status: promotion.status,
        };

        let fn = this.promotionService.updatePromotion(promotion.id, data);
        if (team.id) {
          fn = this.promotionService.updateTeamPromotion(team.id, promotion.id, data);
        }

        return forkJoin([of(promotion), of(team), fn]);
      }),
      switchMap(([promotionData, team, { promotion }]: any[]) => {
        const p = [];
        if (promotionData.mediaToCreate) {
          promotionData.mediaToCreate.forEach(media => {
            p.push(this.promotionService.addMediasToPromotion(team.id, promotion.id, media));
          });
        }
        return forkJoin([of(promotionData), of(team), of(promotion), ...p]);
      }),
      switchMap(([promotionData, team, promotion]: any[]) => {
        if (promotionData.mediasToDelete && promotionData.mediasToDelete.length) {
          const req = [];
          promotionData.mediasToDelete.forEach(media => {
            req.push(this.promotionService.removeMediaToPromotion(team.id, promotion.id, media.id));
          });
          return forkJoin([of(promotion), of(team), ...req]);
        } else {
          return forkJoin([of(promotion), of(team), of(null)]);
        }
      }
      ),
      switchMap(([promotion, team, resDelete]: any) => {
        return [
          UserPromotionActions.updatePromotionSuccess({ promotion }),
          UserPromotionActions.loadTeamPromotions({ teamId: team.id }),
        ];
      }))
  );

  updatePromotionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserPromotionActions.updatePromotionSuccess),
      withLatestFrom(
        this.store$.pipe(select(fromRoot.getActiveTeam))
      ),
      tap(([action, team]) => this.router.navigateByUrl('/account/team/' + team.id + '/promotions'))
    ),
    { dispatch: false }
  );

  addImageToPromotionToCreate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserPromotionActions.addImageToPromotionToCreate),
      map(action => action.image),
      concatMap(([image]: any[]) => {
        const imageSdk = new Image();
        return forkJoin([of(image), from(imageSdk.createImage())])
      }),
      concatMap(([image, res]: any[]) => forkJoin([of(res), this.imageService.uploadImage(res.image.uploadUrl, image)])),
      concatMap(([image, resUpload]: any[]) => {
        return of(UserPromotionActions.addImageToPromotionToCreateSuccess({ ...image }));
      })
    ));

  addVideoToPromotionToCreate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserPromotionActions.addVideoToPromotionToCreate),
      map(action => action.video),
      concatMap((video) => {
        const { type, sourceId } = video;
        if (type === 'Youtube') {
          const thumbnail = 'https://i1.ytimg.com/vi/' + sourceId + '/2.jpg';
          const videoToReturn = { ...video, thumbnail };
          return of(UserPromotionActions.addVideoToPromotionToCreateSuccess({ video: videoToReturn }));
        } else if (type === 'Vimeo') {
          return forkJoin([this.vimeoService.getThumbnailVimeo(video.sourceId)]).pipe(map(([res]: any[]) => {
            const thumbnail = res.thumbnail_url.replace('https://', '');
            const videoToReturn = { ...video, thumbnail };
            return UserPromotionActions.addVideoToPromotionToCreateSuccess({ video: videoToReturn });
          }));
        }
      })
    ));

  addImageToPromotionToUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserPromotionActions.addImageToPromotionToUpdate),
      map(action => action.image),
      concatMap(([image]: any[]) => {
        const imageSdk = new Image();
        return forkJoin([of(image), from(imageSdk.createImage())])
      }),
      concatMap(([image, res]: any[]) => forkJoin([of(res), this.imageService.uploadImage(res.image.uploadUrl, image)])),
      concatMap(([image, resUpload]: any[]) => {
        return of(UserPromotionActions.addImageToPromotionToUpdateSuccess({ ...image }));
      })
    ));

  addVideoToPromotionToUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserPromotionActions.addVideoToPromotionToUpdate),
      map(action => action.video),
      concatMap((video) => {
        const { type, sourceId } = video;
        if (type === 'Youtube') {
          const thumbnail = 'https://i1.ytimg.com/vi/' + sourceId + '/2.jpg';
          const videoToReturn = { ...video, thumbnail };
          return of(UserPromotionActions.addVideoToPromotionToUpdateSuccess({ video: videoToReturn }));
        } else if (type === 'Vimeo') {
          return forkJoin([this.vimeoService.getThumbnailVimeo(video.sourceId)]).pipe(map(([res]: any[]) => {
            const thumbnail = res.thumbnail_url; // .replace('https://', '')
            const videoToReturn = { ...video, thumbnail };
            return UserPromotionActions.addVideoToPromotionToUpdateSuccess({ video: videoToReturn });
          }));
        }
      })
    ));

  deletePromotion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserPromotionActions.deletePromotion),
      withLatestFrom(
        this.store$.pipe(select(fromUserPromotions.getPromotionToDelete)),
        this.store$.pipe(select(fromRoot.getActiveTeam))
      ),
      concatMap(([action, promotionToDelete, team]) => {
        return forkJoin([this.promotionService.deletePromotion(team.id, promotionToDelete.id), of(team)]);
      }),
      concatMap(([res, team]: any) => {
        return [
          UserPromotionActions.deletePromotionSuccess({ promotion: res }),
          UserPromotionActions.loadTeamPromotions({ teamId: team.id })
        ];
      }))
  );

  constructor(
    private actions$: Actions,
    private readonly promotionService: PromotionService,
    private readonly vimeoService: VimeoApiService,
    private imageService: ImageService,
    private store$: Store<fromUserPromotions.State & fromRoot.State>,
    private router: Router
  ) { }

}
